import apiService, { ApiError } from './apiService';

export type ConversationChannel = 'phone' | 'whatsapp';

/** Handoff state of a WhatsApp contact (contract livecenter §0/§4). */
export type TakeoverStateName = 'requested' | 'staff' | 'callback';

/** What the running deadline means: `wait` (requested, agent steps in), `idle`
 *  (staff, quiet chat → back to the agent), `customer_waiting` (staff, the
 *  customer wrote and nobody answered). `null` for `callback` (no deadline). */
export type TakeoverDeadlineKind = 'wait' | 'idle' | 'customer_waiting';

/**
 * Handoff of a WhatsApp contact (livecenter contract §4). `requested` and
 * `staff` pause the agent; `callback` does not (the agent keeps serving the
 * customer while the callback request waits). The livecenter fields are
 * optional so an older payload (live-chat contract §2) still type-checks:
 * a missing `state` means `staff`.
 */
export interface TakeoverState {
  active: boolean;
  state?: TakeoverStateName | null;
  /** E-mail of the business account that took over. */
  taken_by: string | null;
  started_at: string | null;
  expires_at: string | null;
  deadline_at?: string | null;
  deadline_kind?: TakeoverDeadlineKind | null;
  requested_at?: string | null;
  request_reason?: string | null;
  request_summary?: string | null;
  callback_at?: string | null;
  last_staff_at?: string | null;
  last_customer_at?: string | null;
  customer_waiting_since?: string | null;
}

export interface ConversationListItem {
  id: number;
  type: ConversationChannel;
  customer_phone: string;
  customer_name: string | null;
  message_count: number;
  last_message_at: string | null;
  status: string | null;
  preview: string | null;
  /** Produced by the Test-Harness (real agent, text-driven) → "Test" badge. */
  is_test?: boolean;
  /** WhatsApp only: staff currently holds the thread. */
  takeover_active?: boolean;
  /** Last message written by the customer (drives the 24 h reply window). */
  last_customer_message_at?: string | null;
  /** `group=contact` (livecenter §5): the WhatsApp contact this row stands for.
   *  Issued by the server; the web app never builds one itself. */
  contact_key?: string | null;
  takeover_state?: TakeoverStateName | null;
  /** `requested_at` / `callback_at` / `customer_waiting_since`, whichever applies. */
  waiting_since?: string | null;
  /** Number of sessions (conversation rows) behind this WhatsApp contact. */
  session_count?: number | null;
  /** Agent line the customer wrote/called to, when the server sends it. */
  agent_number?: string | null;
}

export interface ConversationListResponse {
  status: string;
  items: ConversationListItem[];
}

/**
 * What happened in the conversation besides speech. Set only when role === 'event'.
 * Phone: asr_filtered | tool | transfer | end. Live chat: takeover_started |
 * takeover_released | bot_suppressed (the held bot reply is in `content`).
 * Handoff (livecenter): handoff_requested | handoff_callback | handoff_all_busy |
 * handoff_declined | callback_resolved.
 */
export type TranscriptEventKind =
  | 'asr_filtered'
  | 'tool'
  | 'transfer'
  | 'end'
  | 'takeover_started'
  | 'takeover_released'
  | 'bot_suppressed'
  | 'handoff_requested'
  | 'handoff_callback'
  | 'handoff_all_busy'
  | 'handoff_declined'
  | 'callback_resolved';

/** `staff` = a salon employee wrote from the web app (author in `author`). */
export type TranscriptRole = 'user' | 'assistant' | 'staff' | 'event';

/**
 * How far a message we sent got (delivery contract 30.09.): `accepted` = Meta
 * took it, nothing reported yet; then `sent` < `delivered` < `read`; `failed`
 * beats everything (Meta will not deliver it).
 */
export type DeliveryStatus = 'accepted' | 'sent' | 'delivered' | 'read' | 'failed';

/** Aggregate over all WhatsApp messages (`wamids`) one transcript row consists of. */
export interface MessageDelivery {
  status: DeliveryStatus;
  /** Only with `failed`: Meta's error code and our German wording of it. */
  error_code?: number | null;
  reason?: string | null;
}

/** State of the WhatsApp number behind a contact (`ok` also while unknown). */
export type ChannelHealthState = 'ok' | 'blocked';

export interface ChannelHealth {
  state: ChannelHealthState;
  /** German, for the UI ("WhatsApp-Konto von Meta gesperrt"). */
  reason?: string | null;
  /** Meta's phone status (`BANNED`, …); diagnostics only. */
  meta_status?: string | null;
  since?: string | null;
}

export interface TranscriptMessage {
  /** 'event' rows are diagnostics (filtered speech, tool call, transfer, end,
   *  takeover changes), rendered as a system line rather than a chat bubble. */
  role: TranscriptRole;
  content: string;
  ts?: string | null;
  kind?: TranscriptEventKind | string | null;
  /** E-mail of the staff member; only set for role === 'staff'. */
  author?: string | null;
  /** WhatsApp reply buttons the customer got with an agent message (titles). */
  buttons?: string[] | null;
  /** Session (conversation row) the message belongs to; set in contact threads. */
  conversation_id?: number | null;
  /** WhatsApp message ids of what we sent (one reply can be several messages). */
  wamids?: string[] | null;
  /** Only rows with `wamids`: how far the message got. */
  delivery?: MessageDelivery | null;
}

export interface ConversationRecord {
  id: number;
  type: ConversationChannel;
  call_sid: string | null;
  messages: TranscriptMessage[];
  /** Tail of the preceding WhatsApp session, shown dimmed above the transcript
   * so a follow-up like "mach doch 13 Uhr" is still understandable. */
  context_messages?: TranscriptMessage[];
  prev_conversation_id?: number | null;
  summary: string | null;
  end_reason_text: string | null;
  status: string | null;
  started_at: string | null;
  ended_at: string | null;
  last_message_at: string | null;
  /** Produced by the Test-Harness (real agent, text-driven) → "Test" badge. */
  is_test?: boolean;
  /** Live chat (WhatsApp): current takeover, `null` when the agent is in charge.
   *  `undefined` = the backend does not serve live chat (fields absent). */
  takeover?: TakeoverState | null;
  /** Last customer message + 24 h (Meta customer-service window); `null` for phone. */
  reply_window_until?: string | null;
  /** `type === 'whatsapp'` and the reply window is open. */
  can_reply?: boolean;
  /** Live chat is switched on for this salon; the only flag that shows the
   *  takeover strip and the composer. */
  live_chat_enabled?: boolean;
}

export interface ConversationDetailResponse {
  status: string;
  customer_phone: string;
  customer_name: string | null;
  record: ConversationRecord;
  /** WhatsApp: the contact this session belongs to, when the server sends it
   *  (lets an old `?c=<id>` link open the continuous contact chat). */
  contact_key?: string | null;
}

/** One session (conversation row) of a WhatsApp contact (livecenter §5). */
export interface ContactSession {
  conversation_id: number;
  started_at: string | null;
  last_message_at: string | null;
  status: string | null;
  summary: string | null;
  end_reason_text: string | null;
  is_test?: boolean;
}

/** `GET /api/conversations/contacts/{contact_key}/thread` (livecenter §5):
 *  the continuous WhatsApp chat of one contact across its sessions. */
export interface ContactThread {
  contact_key: string;
  customer_phone: string;
  customer_name: string | null;
  agent_number: string | null;
  /** Oldest first. */
  sessions: ContactSession[];
  /** Chronological across the sessions; each carries its `conversation_id`. */
  messages: TranscriptMessage[];
  /** Older sessions exist (page with `before=<oldest conversation_id>`). */
  has_more: boolean;
  takeover: TakeoverState | null;
  /** Reply window computed over ALL sessions of the contact. */
  reply_window_until: string | null;
  can_reply: boolean;
  live_chat_enabled: boolean;
  handoff_enabled: boolean;
  /** The contact's agent number at Meta; missing/`null` = treat as `ok`. */
  channel?: ChannelHealth | null;
}

/** Answer of every contact action (livecenter §5). */
export interface ContactActionResponse {
  status: string;
  takeover: TakeoverState | null;
  /** Only for `POST …/messages`. */
  message?: TranscriptMessage;
}

export type LiveCallStatus = 'live' | 'transferring' | 'ended';

/** A phone call as the live view sees it (`live_calls`, livecenter §1/§5). */
export interface LiveCallInfo {
  call_sid: string;
  customer_phone: string;
  customer_name: string | null;
  agent_number: string | null;
  started_at: string | null;
  ended_at?: string | null;
  status: LiveCallStatus;
  /** Stored transcript once the call ended (`call-record`). */
  conversation_id?: number | null;
  last_step?: string | null;
}

/** A finished line of a live call; `seq` counts from 1 per call. */
export interface LiveCallTurn {
  seq: number;
  role: 'user' | 'assistant';
  content: string;
  ts: string | null;
}

/** A tool step of the agent, e.g. `{label: "sucht Termine", tool: "AppointmentSuggestion"}`. */
export interface LiveCallStep {
  label: string;
  tool: string | null;
  ts: string | null;
}

/** `GET /api/live/calls/{call_sid}` (livecenter §5). */
export interface LiveCallResponse {
  call: LiveCallInfo;
  turns: LiveCallTurn[];
  steps: LiveCallStep[];
}

export type LiveChatErrorCode =
  | 'reply_window_closed'
  | 'rate_limited'
  | 'invalid_text'
  | 'not_available'
  | 'phone_conversation'
  | 'whatsapp_channel_blocked'
  | 'unknown';

/** Typed failure of a live-chat action, mapped from the contract's HTTP codes. */
export class LiveChatError extends Error {
  readonly code: LiveChatErrorCode;
  readonly status: number | null;

  constructor(code: LiveChatErrorCode, message: string, status: number | null = null) {
    super(message);
    this.name = 'LiveChatError';
    this.code = code;
    this.status = status;
  }
}

/** German copy per error code, ready for a toast or inline hint. */
export const LIVE_CHAT_ERROR_MESSAGES: Record<LiveChatErrorCode, string> = {
  reply_window_closed:
    'Antwortfenster geschlossen – der Kunde hat seit über 24 Stunden nicht geschrieben.',
  rate_limited: 'Zu viele Nachrichten in kurzer Zeit – bitte einen Moment warten.',
  invalid_text: 'Die Nachricht ist leer oder länger als 4096 Zeichen.',
  not_available: 'Live-Chat ist für dieses Konto noch nicht freigeschaltet.',
  phone_conversation: 'Telefonate können nicht übernommen werden.',
  whatsapp_channel_blocked:
    'Nicht gesendet – diese WhatsApp-Nummer ist gerade nicht erreichbar. Nachrichten kommen nicht an.',
  unknown: 'Die Aktion ist fehlgeschlagen. Bitte erneut versuchen.',
};

export function toLiveChatError(error: unknown): LiveChatError {
  if (error instanceof LiveChatError) return error;
  const status = error instanceof ApiError ? error.status : null;
  const message = error instanceof Error ? error.message : String(error);
  const code: LiveChatErrorCode =
    // Also a 409: told apart by its detail, before the reply-window rule.
    message.includes('whatsapp_channel_blocked')
      ? 'whatsapp_channel_blocked'
      : status === 409 || message === 'reply_window_closed'
        ? 'reply_window_closed'
        : status === 429
          ? 'rate_limited'
          : status === 422
            ? 'invalid_text'
            : status === 404
              ? 'not_available'
              : status === 400
                ? 'phone_conversation'
                : 'unknown';
  return new LiveChatError(code, LIVE_CHAT_ERROR_MESSAGES[code], status);
}

export const MAX_LIVE_CHAT_MESSAGE_LENGTH = 4096;
/** Sessions per page of a contact thread (initial load and every "load older"). */
export const CONTACT_THREAD_PAGE_SESSIONS = 3;

/** Path segment of a contact: the key contains `:` and must stay one segment. */
function contactPath(contactKey: string): string {
  return `/conversations/contacts/${encodeURIComponent(contactKey)}`;
}

function validatedText(text: string): string {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > MAX_LIVE_CHAT_MESSAGE_LENGTH) {
    throw new LiveChatError('invalid_text', LIVE_CHAT_ERROR_MESSAGES.invalid_text, 422);
  }
  return trimmed;
}

async function contactAction(
  contactKey: string,
  action: string,
  body: Record<string, unknown> = {},
): Promise<ContactActionResponse> {
  try {
    return await apiService.post<ContactActionResponse>(`${contactPath(contactKey)}/${action}`, body);
  } catch (error) {
    throw toLiveChatError(error);
  }
}

export type ConversationListGroup = 'contact';

const conversationService = {
  /**
   * `group='contact'` (livecenter §5): WhatsApp as one row per contact (newest
   * activity), phone calls one row per call. Without it the list is unchanged.
   */
  list: async (
    type?: ConversationChannel,
    q?: string,
    limit = 50,
    offset = 0,
    agentNumber?: string | null,
    group?: ConversationListGroup,
  ): Promise<ConversationListResponse> => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (q && q.trim()) params.set('q', q.trim());
    // Without an agent number every conversation is listed, including the ones
    // recorded before conversations carried the number they came in on.
    if (agentNumber) params.set('agent_number', agentNumber);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    if (group) params.set('group', group);
    return apiService.get<ConversationListResponse>(`/conversations?${params.toString()}`);
  },

  getDetail: async (conversationId: number): Promise<ConversationDetailResponse> => {
    return apiService.get<ConversationDetailResponse>(`/conversations/${conversationId}`);
  },

  /**
   * The continuous chat of a WhatsApp contact. `before` pages to the sessions
   * older than that conversation id; `sessions` is how many to load per page.
   */
  getContactThread: async (
    contactKey: string,
    opts: { before?: number | null; sessions?: number } = {},
  ): Promise<ContactThread> => {
    const params = new URLSearchParams();
    if (opts.before != null) params.set('before', String(opts.before));
    params.set('sessions', String(opts.sessions ?? CONTACT_THREAD_PAGE_SESSIONS));
    return apiService.get<ContactThread>(`${contactPath(contactKey)}/thread?${params.toString()}`);
  },

  /** Staff takes the contact (idempotent). */
  contactTakeover: (contactKey: string) => contactAction(contactKey, 'takeover'),

  /** Hand the contact back to the agent (idempotent). */
  contactRelease: (contactKey: string) => contactAction(contactKey, 'release'),

  /** +5 min on `deadline_at`; only while `requested` / `staff`. */
  contactExtend: (contactKey: string) => contactAction(contactKey, 'extend'),

  /** Staff is typing: moves `deadline_at` to at least now + 2 min (server allows one per 20 s). */
  contactTyping: (contactKey: string) => contactAction(contactKey, 'typing'),

  /** The callback request is done (e.g. the customer was called). */
  resolveCallback: (contactKey: string, note?: string | null) =>
    contactAction(contactKey, 'callback/resolve', note ? { note } : {}),

  /** Send as the salon over the contact's agent number; opens/extends the takeover. */
  contactSendMessage: async (contactKey: string, text: string): Promise<ContactActionResponse> =>
    contactAction(contactKey, 'messages', { text: validatedText(text) }),

  /** Transcript so far of a running call; 404 when it is not this salon's call. */
  getLiveCall: async (callSid: string): Promise<LiveCallResponse> => {
    return apiService.get<LiveCallResponse>(`/live/calls/${encodeURIComponent(callSid)}`);
  },
};

export default conversationService;
